<template>
  <v-dialog
    v-model="showFilesPreviewDialog"
    max-width="880"
    class="v-dialog"
    @click:outside="closeDialog"
  >
    <v-card
      class="v-card"
      elevation="10"
    >
      <v-card-title
        class="v-card-title"
      >
        {{ dialogTitle }}
        <v-spacer />

        <div
          class="x-image"
        >
          <image-field
            src="/img/icons/x-icon-image.svg"
            contain
            width="30"
            height="30"
            @onClick="closeDialog"
          />
        </div>
      </v-card-title>
      <v-card-text
        class="v-card-text"
      >
        <!--    Loading spinner button-->
        <v-overlay
          v-if="showLoader"
          opacity="0.2"
          absolute
          color="#c8c9cb"
        >
          <v-progress-circular
            indeterminate
            :color="'var(--v-primary-base)'"
            size="60"
          />
        </v-overlay>
        <template
          v-if="existingFiles && existingFiles.length"
        >
          <v-row
            v-for="(file, fileKey) in existingFiles"
            :key="'file_' + fileKey"
          >
            <!--            File name with its icon placed at beginning of the file row-->
            <v-col
              class="d-flex flex-row justify-start file-name-section"
              cols="10"
              style="padding: 0 !important;"
            >
              <div
                class="file-icon-div-wrapper"
              >
                <image-field
                  src="/img/icons/pdf-preview-icon.svg"
                  width="30"
                  height="30"
                />
              </div>
              <span
                class="d-flex align-center file-name"
              >
                {{ file && file.name ? file.name : '' }}
              </span>
            </v-col>

            <!--            Buttons placed on the end of file row-->
            <v-col
              class="d-flex flex-column file-buttons-section"
              cols="2"
              :class="{ 'justify-center': !canShowDownloadBtn(file) }"
            >
              <!-- Download button -->
              <universal-button
                v-if="canShowDownloadBtn(file)"
                type="base"
                color="#65AD45"
                append-icon="mdi-arrow-down-bold-box-outline"
                style="color: #FFFFFF;"
                class="mb-1"
                @click="downloadFile(fileKey)"
              />

              <!-- Remove button -->
              <universal-button
                type="base"
                color="#EF4444"
                append-icon="mdi-delete"
                style="color: #FFFFFF;"
                @click="removeFile(fileKey)"
              />
            </v-col>
          </v-row>
        </template>
        <!--        no data text-->
        <template
          v-else
        >
          <div
            style="text-align: center;"
          >
            {{ $t('base.no_data') }}
          </div>
        </template>
      </v-card-text>
      <v-card-actions
        class="v-card-actions"
      >
        <v-spacer />

        <!--        Close button-->
        <universal-button
          type="base"
          color="#111827"
          style="color: white; width: 130px; height: 40px;"
          :disabled="showLoader"
          :label="$t('base.close')"
          @click="closeDialog"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import ImageField from '@/global/components/fields/ImageField.vue'

export default {
  name: 'FilesViewerDialog',

  components: {
    ImageField,
    UniversalButton
  },

  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    showLoader: {
      type: Boolean,
      default: false
    },
    tabKey: {
      type: String,
      default: ''
    },
    fieldKey: {
      type: String,
      default: ''
    },
    files: {
      type: Array,
      default: () => ([])
    },
    field: {
      type: Object,
      default: null
    },
    dialogTitle: {
      type: String,
      default: ''
    },
    removeCallback: {
      type: Function,
      default: () => {}
    },
    removeApiConfig: {
      type: Object,
      default: () => ({})
    }
  },

  emits: [
    'close'
  ],

  computed: {
    showFilesPreviewDialog () {
      return this.showDialog
    },

    existingFiles: {
      get () {
        return this.files
      },
      set (val) {
        return val
      }
    }
  },

  methods: {
    closeDialog () {
      this.$emit('close')
    },

    canShowDownloadBtn (file) {
      return !!file?.original_url
    },

    downloadFile (fileKey) {
      const link = document.createElement('a')

      // File that is uploaded by user is stored as an object with data and name properties
      if (this.existingFiles[fileKey] && this.existingFiles[fileKey].data) {
        link.href = this.existingFiles[fileKey].data
        link.download = this.existingFiles[fileKey].name
      }
      // File retrieved from server and its represented as a url string
      else if (this.existingFiles?.[fileKey]?.original_url) {
        link.href = this.existingFiles[fileKey].original_url
        link.download = this.existingFiles[fileKey]?.name || 'file.pdf'
      }

      link.click()
    },

    removeFile (fileKey) {
      if (fileKey !== null) {
        const fileToRemove = this.files[fileKey]
        if (typeof this.removeCallback === 'function' && fileToRemove) {
          this.removeCallback({
            removeApiConfig: this.removeApiConfig,
            file: fileToRemove,
            index: fileKey,
            tabKey: this.tabKey,
            fieldKey: this.fieldKey,
            field: this.field
          })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-dialog {
    max-height: 50vh !important;
  }
}
  .file-name-section {
    background-color: #FFFFFF;
    border-radius: 16px;
    border: 1px solid #E5E7EB;
    margin-bottom: 15px;
    margin-top: 15px;
  }

  .file-buttons-section {
    align-items: center;
  }

  .file-icon-div-wrapper {
    align-content: center;
    justify-items: center;
    background-color: #FEF2F2;
    margin: 5px;
    border-radius: 12px;
    min-height: 60px;
    min-width: 60px;
  }

  .file-icon {
    font-size: 35px;
  }

  .v-card-text {
    background-color: #F9FAFB;
    padding: 24px 40px 40px 40px !important;
  }

  .v-card-title {
    font-family: 'Satoshi', sans-serif;
    font-size: 18px !important;
    font-weight: 700 !important;
    letter-spacing: 0;
    border-bottom: 1px solid #E5E7EB;
  }

  .v-card-actions {
    font-size: 20px;
    border-top: 1px solid #E5E7EB;
  }

  .v-card {
    border-radius: 16px;
  }

  .file-name {
    font-size: 16px;
    font-weight: 500;
  }

.x-image:hover {
  cursor: pointer;
}
</style>
